import _ from 'lodash';
import { RRule } from 'rrule';
import moment from 'moment';
import memoizeOne from 'memoize-one';
import * as RRuleFunctions from '../RRuleFunctions';
import * as UtilityFunctions from '../UtilityFunctions';
import * as TaskUtilities from '../TaskUtilities';
import { COLOR_PALETTE, ACCEPTANCE_STATUS } from '../constants';
import { TASK_PRIORITY_KEYED, STATUS_LIST } from '../enums';
import { findTimeForReminder } from '../TaskUtilities';

function setRecurringTimeObjToTask(oldFlatTask, task) {
  const flatTask = _.clone(oldFlatTask);
  const rrule = RRule.fromString(task.schedule.rrule);
  const rruleObject = rrule.origOptions;
  const { nextRecurrenceDate, prevRecurrenceDate } = task; // rrule.after(new Date(), true);
  // const prevRecurrenceDate = task.prevRecurrenceDate; // rrule.before(new Date(), false);
  const startDate = moment(rruleObject.dtstart).format('YYYY-MM-DD');
  const endDate = moment(rruleObject.until).format('YYYY-MM-DD');
  let frequency = null;
  if (rruleObject.freq === RRule.WEEKLY || rruleObject.freq === RRule.MONTHLY) {
    const dateStartAtCurrentTZ = moment(rruleObject.dtstart).date();
    const dateStartAtUTC = moment(rruleObject.dtstart).utc().date();
    if (dateStartAtCurrentTZ > dateStartAtUTC) {
      if (rruleObject.freq === RRule.WEEKLY) {
        for (let i = 0; i < rruleObject.byweekday.length; i += 1) {
          const weekday = rruleObject.byweekday[i];
          rruleObject.byweekday[i] = RRuleFunctions.getRRuleWeekDay(
            RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 1
          );
        }
      } else {
        // if freq is monthly
        const monthday = rruleObject.bymonthday;
        rruleObject.bymonthday = (monthday % 30) + 1;
      }
    } else if (dateStartAtCurrentTZ < dateStartAtUTC) {
      if (rruleObject.freq === RRule.WEEKLY) {
        for (let i = 0; i < rruleObject.byweekday.length; i += 1) {
          const weekday = rruleObject.byweekday[i];
          rruleObject.byweekday[i] = RRuleFunctions.getRRuleWeekDay(
            RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 6
          );
        }
      } else {
        // if freq is monthly
        const monthday = rruleObject.bymonthday;
        rruleObject.bymonthday = ((monthday + 28) % 30) + 1;
      }
    }
  }
  if (rruleObject.freq === RRule.WEEKLY) {
    const days = rruleObject.byweekday.map((n) => RRuleFunctions.dayNoWeekDayMap[n.weekday].substr(0, 3)).join(', ');
    frequency = `Weekly (${days})`;
  } else if (rruleObject.freq === RRule.MONTHLY) {
    frequency = `Monthly (on ${UtilityFunctions.getOrdinalNumber(rruleObject.bymonthday)} day, ${
      rruleObject.interval === 1
        ? 'every month'
        : `every ${UtilityFunctions.getOrdinalNumber(rruleObject.interval)} month`
    })`;
  } else if (rruleObject.freq === RRule.DAILY) {
    frequency = `Daily (${
      rruleObject.interval === 1 ? 'everyday' : `every ${UtilityFunctions.getOrdinalNumber(rruleObject.interval)} day`
    })`;
  }
  // frequency = rrule.toText();
  Object.assign(flatTask, {
    startDate,
    endDate,
    frequency,
    nextRecurrenceDate:
      nextRecurrenceDate && !task.recurrenceInactive ? moment(nextRecurrenceDate).format('YYYY-MM-DD') : '-',
    prevRecurrenceDate: prevRecurrenceDate ? moment(prevRecurrenceDate).format('YYYY-MM-DD') : '-',
  });
  return flatTask;
}

function setChildTasksToParentTask(oldFlatTasks, includeParentTask) {
  const flatTasks = _.clone(oldFlatTasks);
  const flatTaskDataIdMap = _.keyBy(flatTasks, 'id');
  let colorPaletteIndex = -1;
  for (let i = 0; i < flatTasks.length; i += 1) {
    const flatTask = flatTasks[i];
    if (flatTask.parentTaskId) {
      const parentTask = flatTaskDataIdMap[flatTask.parentTaskId];
      if (parentTask) {
        flatTask.parentTask = parentTask;
        if (!parentTask.color) {
          colorPaletteIndex = (colorPaletteIndex + 1) % COLOR_PALETTE.length;
          parentTask.color = COLOR_PALETTE[colorPaletteIndex];
        }
      } else {
        delete flatTask.parentTaskId;
        delete flatTask.workflowVersion;
      }
    }
  }
  const allChildTasks = flatTasks.filter((t) => t.parentTaskId);
  const childrenWorkflowStepIdMap = _.keyBy(allChildTasks, (t) => `${t.parentTaskId}_${t.workflowStepId}`);
  flatTasks
    .filter((t) => TaskUtilities.isTaskParent(t))
    .forEach((t) => {
      const workflow = t?.taskType?.workflowVersions?.find((wfv) => wfv.version === 'v1');
      if (!workflow) return;
      const { workflowSteps = [] } = workflow;
      workflowSteps.forEach(({ workflowStepId }, index) => {
        const childTask = childrenWorkflowStepIdMap[`${t.id}_${workflowStepId}`];
        if (childTask) childTask.childTaskSeqNo = index;
      });
    });
  if (includeParentTask) {
    return flatTasks;
  }
  return flatTasks.filter((t) => !TaskUtilities.isTaskParent(t));
}

function setAcceptReject(task) {
  const acceptedUser = (task.assigneeIds || []).filter((e) => !e.isDeleted && e.isAccepted === true);
  if (acceptedUser && acceptedUser.length) {
    return ACCEPTANCE_STATUS.ACCEPTED.label;
  }
  const filteredAssignees = (task.assigneeIds || []).filter((assignee) => !assignee.isDeleted);
  if (!_.size(filteredAssignees) && _.size(task.rejectedIds)) {
    return ACCEPTANCE_STATUS.REJECTED.label;
  }
  const pendingAcceptance =
    _.get(task, 'taskType.needAcceptance', false) &&
    _.size(filteredAssignees) &&
    task.status === STATUS_LIST.READYTOACCEPT.key;

  if (pendingAcceptance) {
    return ACCEPTANCE_STATUS.PENDING_ACCEPTANCE.label;
  }
  return '';
}

function getFullName(assigneeObj) {
  return `${assigneeObj.firstName ?? ''} ${assigneeObj.lastName ?? ''}`;
}

function getEnrichedAssignedIds(task, vendorsDict) {
  const assigneeObj = UtilityFunctions.parseJSONStringToObject(task.assignee);
  return task.assigneeIds
    .filter((a) => !a.isDeleted && assigneeObj[a.assigneeId])
    .map((a) => {
      const v = a.vendorId ? vendorsDict?.[a.vendorId]?.name ?? '' : '';
      return {
        ...a,
        fullName: getFullName(assigneeObj[a.assigneeId]),
        isEquipmentResource: assigneeObj[a.assigneeId].isEquipmentResource || false,
        vendorName: v,
      };
    })
    .sort((a, b) => {
      const condition1 = a.isEquipmentResource ? 1 : 0;
      const condition2 = b.isEquipmentResource ? 1 : 0;
      return condition2 - condition1;
    });
}

function taskSortFunction(task1, task2) {
  let sortOrder = (task2.parentTaskId ?? '').localeCompare(task1.parentTaskId ?? '');
  if (sortOrder !== 0) return sortOrder;
  if (task1.parentTaskId && task2.parentTaskId) {
    sortOrder = task1.childTaskSeqNo - task2.childTaskSeqNo;
  }
  if (sortOrder !== 0) return sortOrder;
  const taskAPriorityOrder = _.get(TASK_PRIORITY_KEYED, `${task1.priority}.order`, 0);
  const taskBPriorityOrder = _.get(TASK_PRIORITY_KEYED, `${task2.priority}.order`, 0);
  sortOrder = taskBPriorityOrder - taskAPriorityOrder;
  if (sortOrder !== 0) return sortOrder;
  return Date.now(task2.modifiedOn) - Date.now(task2.modifiedOn);
}
function getAreaNames(assetAreas, areaDict) {
  const areaNames = assetAreas
    .filter((area) => !area.isDeleted)
    .map((area) => _.get(areaDict, `${area.areaId}.name`, ''));
  return (areaNames || []).filter((area) => area);
}

function getWatchersName(watcherIds, watcherDetailsDict) {
  const watcherNames = (watcherIds ?? [])
    .map((id) => {
      const reqWatcher = watcherDetailsDict[id];
      if (reqWatcher) {
        return `${reqWatcher?.firstName ?? ''} ${reqWatcher?.lastName ?? ''}`;
      }
      return '';
    })
    .filter((name) => name);
  return watcherNames.join(', ');
}

function getEnrichedAssetLevelsInfo(assetLevelsInfo, assetLevelsDict) {
  const enrichedAssetLevelsInfo = {};
  Object.keys(assetLevelsInfo || {}).forEach((key) => {
    const reqAsset = assetLevelsDict[assetLevelsInfo[key]];
    if (reqAsset) {
      enrichedAssetLevelsInfo[key] = `${reqAsset?.name ?? ''}`;
    } else {
      enrichedAssetLevelsInfo[key] = '';
    }
  });
  return enrichedAssetLevelsInfo;
}

export function getFlatTaskList({
  tasks,
  includeParentTask = false,
  allAreas = [],
  vendorsDict = {},
  watcherDetailsDict = {},
  assetLevelsDict = {},
}) {
  let taskRows = [];
  const tasksArraySorted = tasks;
  const areaDict = _.keyBy(allAreas, 'id');
  tasksArraySorted.forEach((task) => {
    // if assignee exists, looping on different assigned users
    if (!task.taskType) {
      console.debug(`task with id ${task.id} doesn't have task type`);
      return;
    }
    const areaNames = getAreaNames(_.get(task, 'asset.areas', []) || [], areaDict);
    findTimeForReminder(task);
    const data = UtilityFunctions.parseJSONStringToObject(task.data);
    const flatTask = {
      ...task,
      areaNames,
      data,
      globalAttributes: UtilityFunctions.parseJSONStringToObject(task.globalAttributes),
      reminder: findTimeForReminder(task),
      acceptRejectStatus: setAcceptReject(task),
      assigneeIds: getEnrichedAssignedIds(task, vendorsDict),
      watchersName: getWatchersName(task.watcherIds, watcherDetailsDict),
      assetLevelsEnrichedInfo: getEnrichedAssetLevelsInfo(task.assetLevelsInfo, assetLevelsDict),
    };
    taskRows.push(flatTask);
  });
  taskRows = setChildTasksToParentTask(taskRows, includeParentTask);
  taskRows = taskRows.sort(taskSortFunction);
  return taskRows;
}

export const getRecurringFlatTask = memoizeOne((tasks, allAreas = []) => {
  let flatTaskData = [];
  const tasksArraySorted = tasks.sort((a, b) => a.createdOn < b.createdOn);
  const areaDict = _.keyBy(allAreas, 'id');
  tasksArraySorted.forEach((task) => {
    // if assignee exists, looping on different assigned users
    if (!task.taskType) {
      console.debug(`task with id ${task.id} doesn't have task type`);
      return;
    }
    const areaNames = getAreaNames(_.get(task, 'asset.areas', []) || [], areaDict);
    const data = UtilityFunctions.parseJSONStringToObject(task.data);
    let flatTask = {
      ...task,
      areaNames,
      data,
      globalAttributes: UtilityFunctions.parseJSONStringToObject(task.globalAttributes),
      assigneeIds: getEnrichedAssignedIds(task),
    };
    flatTask = setRecurringTimeObjToTask(flatTask, task);
    flatTaskData.push(flatTask);
  });
  flatTaskData = setChildTasksToParentTask(flatTaskData, true);
  return flatTaskData;
});

export function getApprovalTaskList(tasks, userId) {
  let taskRows = [];
  const tasksArraySorted = tasks;

  tasksArraySorted.forEach((task) => {
    // if assignee exists, looping on different assigned users
    if (!task.taskType) {
      console.debug(userId);
      console.debug(`task with id ${task.id} doesn't have task type`);
      return;
    }
    const data = UtilityFunctions.parseJSONStringToObject(task.data);
    const flatTask = {
      ...task,
      data,
    };
    taskRows.push(flatTask);
  });
  taskRows = taskRows.sort((a, b) => {
    const taskAPriorityOrder = _.get(TASK_PRIORITY_KEYED, `${a.priority}.order`, 0);
    const taskBPriorityOrder = _.get(TASK_PRIORITY_KEYED, `${b.priority}.order`, 0);
    let sortOrder = taskBPriorityOrder - taskAPriorityOrder;
    if (sortOrder !== 0) return sortOrder;
    sortOrder = new Date(b.modifiedOn) - new Date(a.modifiedOn);
    return sortOrder;
  });
  return taskRows;
}
